// 키 사이에 틱을 준다. FormatTextBox 는 키마다 selectionStart 와 React 상태를
// 갱신하므로 연달아 쏘면 앞 글자가 반영되기 전에 다음이 들어가 값이 뭉개진다.
const KEY_MS = 30;
// 목록이 열리고 React 가 그리는 데 걸리는 시간.
const OPEN_MS = 250;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

type TextControl = HTMLInputElement | HTMLTextAreaElement;

function nativeSetter(element: TextControl): (this: TextControl, value: string) => void {
  const prototype = element instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
  if (!descriptor?.set) throw new Error('value setter 를 찾지 못했다');
  return descriptor.set as (this: TextControl, value: string) => void;
}

function keyCodeOf(character: string): number {
  if (character >= '0' && character <= '9') return 48 + Number(character);
  return character.toUpperCase().charCodeAt(0);
}

function sendKey(element: TextControl, key: string, keyCode: number): void {
  // `which` 는 표준에서 빠져 `KeyboardEventInit` 에 없다. 그래도 넣는다 — 이 앱의
  // 위젯 중에 그것을 읽는 것이 있다. 타입만 넓힌다.
  element.dispatchEvent(new KeyboardEvent('keydown', {
    key, keyCode, which: keyCode, bubbles: true, cancelable: true,
  } as KeyboardEventInit));
}

/**
 * 한 글자씩 눌러 넣고 엔터로 확정한다.
 *
 * `FormatTextBox` 계열(`DateTextBox` 포함, 검색조건의 26%)은 input 의 React
 * onChange 가 빈 함수다. 값은 `onKeyDown` 이 `e.nativeEvent.key` 로 받아 쌓고
 * `keyCode === 13` 에서 확정한다. 실제 앱에서 확인한 경로다.
 *
 * 클릭하면 달력 팝업이 떠 이후 조작을 막으므로 `focus()` 로만 들어간다.
 */
async function typeValue(element: TextControl, value: string): Promise<void> {
  element.focus();
  element.setSelectionRange(element.value.length, element.value.length);
  // 마스크가 있는 칸은 지워도 자리표시가 남는다. 길이보다 넉넉히 보낸다.
  const clears = element.value.length + 4;
  for (let index = 0; index < clears; index += 1) {
    sendKey(element, 'Backspace', 8);
    await sleep(KEY_MS);
  }
  // 구분자는 치지 않는다. 이 경로로 오는 칸은 직접 대입을 되돌리는 칸, 곧 마스크가
  // 있는 칸이고 마스크는 `-` 를 **자기가** 넣는다. 같이 치면 자리가 밀린다 —
  // `2025-08-01` 을 그대로 쳤더니 화면에 `2025-00-80` 이 들어갔고, 8월 조회가
  // 12월 6,577행을 가져왔다. 실제 앱에서 본 것이다.
  for (const character of value.replace(/[^0-9A-Za-z가-힣]/g, '')) {
    sendKey(element, character, keyCodeOf(character));
    await sleep(KEY_MS);
  }
  sendKey(element, 'Enter', 13);
  await sleep(KEY_MS);
  // FormatTextBox는 Enter가 아니라 blur에서 내부 store를 React 상태로 커밋한다.
  // 마지막 필드는 다음 입력이 포커스를 가져가지 않을 수 있으므로 여기서 명시적으로 확정한다.
  element.blur();
  await sleep(KEY_MS);
}

/** 마스크 문자와 구분자를 뺀 알맹이. 화면 표기와 요청 값을 같은 자로 잰다. */
function typed(value: string): string {
  return value.replace(/[^0-9A-Za-z가-힣]/g, '');
}

/**
 * 검색칸에 값을 넣는다.
 *
 * 평범한 `TextBox`(검색조건의 52%)는 React 가 value setter 를 가로채므로
 * prototype 의 원래 setter 를 부른 뒤 input 이벤트를 쏘면 받아들인다.
 *
 * 그 방식이 통하지 않는 위젯은 값을 **디스패치하는 그 순간 되돌린다.** 그래서
 * 되돌아왔는지 바로 확인해 보고, 되돌아왔으면 키 입력으로 넣는다. 어느 위젯인지
 * 미리 알 필요가 없고, 위젯이 바뀌어도 따라간다.
 */
/** 화면이 다시 그린 값이 우리가 넣은 것과 같은가. `2` 와 `2.000` 은 같다. */
function accepted(shown: string, wanted: string): boolean {
  if (shown === wanted) return true;
  const [a, b] = [Number(shown.replace(/,/g, '')), Number(wanted.replace(/,/g, ''))];
  return shown.trim() !== '' && Number.isFinite(a) && Number.isFinite(b) && a === b;
}

export async function setValue(element: TextControl, value: string): Promise<void> {
  // React 는 입력마다 `_valueTracker` 를 달아 두고, 그것과 값이 같으면 change 를
  // 만들지 않는다. setter 만 부르면 트래커가 여전히 옛 값을 들고 있어 조용히 무시되는
  // 칸이 있다 — 견적 수량 칸이 그랬다(DOM 은 2, 화면 상태는 0).
  const tracked = element as TextControl & { _valueTracker?: { setValue(value: string): void } };
  tracked._valueTracker?.setValue(`${value}_`);
  nativeSetter(element).call(element, value);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  if (accepted(element.value, value)) return;
  await typeValue(element, value);
  // `FormatTextBox` 는 props.value 가 바뀔 때마다 내부 store 를 그것으로 되돌린다.
  // 다른 칸이 부른 서버 응답이나 좌표 조회가 입력 도중에 도착하면 그때까지 친 글자가
  // 사라지고 남은 글자만 들어간다 — `20260809` 가 `60809` 로 들어간 것을 봤다.
  // 한 번만 다시 친다. 두 번째도 어긋나면 부르는 쪽이 상태로 판정한다.
  if (!accepted(element.value, value) && typed(element.value) !== typed(value)) {
    await typeValue(element, value);
  }
}

/** 이 앱의 선택 위젯은 onClick 이 아니라 onMouseDown 으로 받는다. */
function press(element: HTMLElement): void {
  for (const type of ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click']) {
    element.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
  }
}

/**
 * 요청한 선택지가 화면에 없다.
 *
 * 문구만 남기지 않고 **실제 선택지를 들고** 던진다. 부르는 쪽이 그것으로 사용자에게
 * 되물을 수 있어야 한다 — 없는 것을 골랐다고 대화를 끝내는 것은 답이 아니다.
 */
export class OptionNotFound extends Error {
  // `#private` 로 두면 앱의 tsc 가 `Private identifiers are only available when
  // targeting ECMAScript 2015 and higher` 로 막는다. 그 리포는 ES5 를 겨눈다.
  private storedField?: string;

  private storedRoute?: string;

  /**
   * 어느 칸이었는지는 `fillConditions` 가 채운다. 여기서는 알 수 없다.
   *
   * 이름이 정해지면 메시지도 다시 쓴다. `선택지 00456 을 찾지 못했다. 있는 것: ` 만
   * 보고는 어느 칸이 걸렸는지 알 수 없어, 실패 하나를 놓고 화면을 뒤져야 했다.
   */
  get field(): string | undefined { return this.storedField; }

  set field(name: string | undefined) {
    this.storedField = name;
    this.message = OptionNotFound.describe(this.wanted, this.options, name, this.storedRoute);
  }

  /**
   * 후보가 비었을 때 **왜** 비었는지. `팝업이 안 열렸다` 와 `그 칸에는 고를 목록이 없다` 는
   * 다른 일이다 — 앞의 것은 다시 열면 되고 뒤의 것은 사람에게 묻는 수밖에 없다.
   * 레인 B 가 채운다.
   */
  reason?: string;

  /**
   * 되묻기 옵션의 라벨→코드. 표 셀 품목처럼 코드로 확정해야 하는 칸이 쓴다.
   * `popup.ts` 가 채운다. 안 채우면 `undefined`(오늘과 동일) — 있는 것만 코드로 확정한다.
   */
  optionCodes?: { label: string; code: string }[];

  /**
   * 어느 화면이었는가. 같은 이름의 칸이 화면마다 다르게 생겼다 —
   * `spjangcd 칸에 고를 목록이 없다` 만 보고 화면 넷을 손으로 열어 봤는데 넷 다
   * 멀쩡했다. 어디서 났는지를 모르면 재현할 수가 없다.
   */
  get route(): string | undefined { return this.storedRoute; }

  set route(path: string | undefined) {
    this.storedRoute = path;
    this.message = OptionNotFound.describe(this.wanted, this.options, this.storedField, path);
  }

  private static describe(wanted: string, options: string[], field?: string, route?: string): string {
    const where = field ? `${field} 칸의 ` : '';
    const screen = route ? ` (${route})` : '';
    return options.length
      ? `${where}선택지 ${wanted} 을 찾지 못했다${screen}. 있는 것: ${options.join(', ')}`
      : `${where}선택지 ${wanted} 을 찾지 못했다${screen}. 그 칸에는 고를 목록이 없다.`;
  }

  constructor(readonly wanted: string, readonly options: string[]) {
    super(OptionNotFound.describe(wanted, options));
    this.name = 'OptionNotFound';
  }
}

function normalized(value: string): string {
  return value.replace(/[^0-9A-Za-z가-힣]/g, '').toLocaleLowerCase();
}

/** `안전교육` 이 `안전보건교육` 안에 순서대로 들어 있는가. */
function subsequence(haystack: string, needle: string): boolean {
  let index = 0;
  for (const character of haystack) {
    if (character === needle[index]) index += 1;
    if (index === needle.length) return true;
  }
  return false;
}

/**
 * 표기 차이를 넘어 같은 것을 가리키는가.
 *
 * `안전교육` 과 `안전보건교육` 처럼 사이에 글자가 끼는 경우가 실제로 많아 부분
 * 문자열로는 못 잡는다. 느슨한 판정이지만, 부르는 쪽이 **후보가 정확히 하나일 때만**
 * 쓰므로 여럿에 걸리면 자동으로 고르지 않고 사람에게 묻는다.
 */
function matches(option: string, wanted: string): boolean {
  const [left, right] = [normalized(option), normalized(wanted)];
  if (!left || !right) return false;
  return subsequence(left, right) || subsequence(right, left);
}

/**
 * 목록에서 고르는 칸에 값을 넣는다.
 *
 * `ComboBox` 는 `<select>` 가 아니라 클릭하면 열리는 `<div>` 이고, **선택지의
 * 핸들러가 `onClick` 이 아니라 `onMouseDown`** 이다. `click()` 은 아무 일도
 * 일으키지 않는다. 실제 앱에서 확인한 사실이다.
 *
 * 새 공용 ComboBox는 선택지에 `data-el-option` 내부값도 내보낸다. 따라서 화면에
 * 보이는 글자와 상태값(`전체`/`%`, `발행`/`1`) 어느 쪽으로 요청해도 고를 수 있다.
 * 구형 화면은 표시 문구 선택을 그대로 지원한다.
 */
export async function selectOption(anchor: HTMLElement, label: string): Promise<void> {
  // 목록이 열리기 전 모습을 남겨 둔다. 못 찾았을 때 "무엇이 있었는가" 를 말하려면
  // 열려 있는 지금 읽어야 한다 — 닫으면 선택지는 DOM 에서 사라진다.
  const before = leaves();
  anchor.click();
  await sleep(OPEN_MS);

  // **이 칸의 것만 본다.** 예전에는 페이지 전체의 `[data-el-option]` 을 긁었다.
  // 인사등록에서 `spjangcd` 와 `bankcd` 에 물었더니 둘 다 `여 | 부 | 남 | 양력 …`
  // 을 돌려줬다 — 성별과 양음력 칸의 선택지다(실측 2026-09-01). 그 목록이 사람에게
  // 「고를 수 있는 것은 …」으로 나가고, 고를 때도 남의 칸 선택지를 누를 수 있었다.
  //
  // **고르는 범위와 보여 줄 범위를 나눈다.** 좁힌 것을 고르는 데까지 쓰면, 목록이 화면
  // 밖(포털)에 이미 그려져 있어 새로 나타나지 않는 화면에서 고르지 못한다 — 인사등록
  // 가족 탭의 관계 칸이 그랬다(실측: 완주 8/8 → 4/6).
  const fresh = Array.from(leaves()).filter((element) => !before.has(element));
  const inAnchor = Array.from(anchor.querySelectorAll<HTMLElement>('[data-el-option]'));
  // 확실히 이 칸의 것: 눌러서 새로 나타난 것, 없으면 그 칸 안에 든 것.
  const pool = fresh.length ? fresh : inAnchor;
  // 고를 때만 넓힌다. 여기서 못 찾으면 아예 못 고른다 — 예전 그대로 화면 전체를 본다.
  const reach = pool.length ? pool : Array.from(document.querySelectorAll<HTMLElement>('div,li,span'))
    .filter((element) => element.children.length === 0);
  const textOf = (element: HTMLElement) => (element.textContent ?? '').trim();
  const byValue = reach
    .filter((element) => element.getAttribute('data-el-option') === label)
    .pop();
  const byLabel = reach.filter((element) => textOf(element) === label).pop();
  // `07 (주)대명엘리베이터서비스` 처럼 **코드와 이름을 함께** 말하는 일이 있다 —
  // 계획 문맥에 사업장이 코드·이름 쌍으로 들어 있어서다. 이름만 보면 후보가 둘이 되어
  // (`(주)대명엘리베이터` 도 그 안에 들어 있다) 고르길 포기했다. 앞 낱말이 내부값과
  // 맞으면 그것으로 확정한다.
  const byCodeAndName = byValue ?? byLabel ? undefined : (() => {
    const head = label.trim().split(/\s+/)[0] ?? '';
    const tail = label.trim().slice(head.length).trim();
    if (!head || !tail) return undefined;
    return reach
      .filter((element) => element.getAttribute('data-el-option') === head
        && textOf(element) === tail)
      .pop();
  })();
  const option = byValue ?? byLabel ?? byCodeAndName;
  // 사람에게 보여 줄 것은 **화면에 보이는 글자**다. `001`, `01` 같은 내부값을 내밀면
  // 고를 수가 없다. 보이는 글자가 없는 선택지만 내부값으로 적는다.
  const opened = pool;
  const labelled = pool
    .map((element) => textOf(element) || (element.getAttribute('data-el-option') ?? ''));
  // 목록을 여는 사이에 화면 알림이 뜨면 그 문장까지 선택지로 잡힌다. `있는 것:
  // 조회된 자료가 없습니다` 라는 말이 사용자에게 나간 적이 있다 — 고를 수 있는 것이
  // 아니라 화면이 한 말이다.
  const notice = /없습니다|하십시오|하세요|해주세요|하시겠습니까|입니다\.$/;
  const available = Array.from(new Set([
    ...labelled,
    ...opened.map(textOf),
  ].filter((text) => text && text.length <= 20 && !notice.test(text)))).slice(0, 20);

  // 표기만 다른 값(`안전교육` ↔ `안전보건교육`)까지 되물으면 대화가 늘어진다.
  // 후보가 **정확히 하나**일 때만 대신 골라 준다. 여럿이면 사람이 고를 일이다.
  const near = option ? [] : available.filter((text) => matches(text, label));
  const chosen = option
    ?? (near.length === 1
      ? reach.find((element) => textOf(element) === near[0]
        || element.getAttribute('data-el-option') === near[0])
      : undefined);

  if (!chosen) {
    anchor.click();   // 열어 둔 목록을 닫는다
    throw new OptionNotFound(label, available);
  }
  press(chosen);
  await sleep(OPEN_MS);
}

function leaves(): Set<HTMLElement> {
  return new Set(Array.from(document.querySelectorAll<HTMLElement>('div,li,span'))
    .filter((element) => element.children.length === 0 && (element.textContent ?? '').trim()));
}

/**
 * 목록을 열어 **그 안에 있는 것 중 하나**를 고르고, 고른 글자를 돌려준다.
 *
 * 부르는 쪽이 선택지를 모를 때 쓴다 — 콤보박스의 선택지는 열기 전에는 DOM 에 없다.
 * `preferred` 에 적힌 것이 있으면 그것을, 없으면 첫 번째를 고른다.
 *
 * `avoid` 에 적은 글자는 건너뛴다. **첫 선택지가 가장 좁은 필터인 경우가 흔해서**,
 * 한 번 골라 0행이면 다른 것으로 다시 해 봐야 한다 — 점검계획등록 관측이
 * `미계약`·`미전송` 에 걸려 자료를 통째로 못 봤다(2026-09-22). 건너뛸 것뿐이면
 * 그냥 첫 번째를 고른다. 아무것도 못 고르는 것보다 낫다.
 *
 * 열기 전후의 잎 요소 차이가 곧 선택지다. 목록이 열리지 않거나 비어 있으면 던진다.
 */
export async function selectAny(
  anchor: HTMLElement,
  preferred: string[] = [],
  avoid: string[] = [],
): Promise<string> {
  const before = leaves();
  anchor.click();
  await sleep(OPEN_MS);

  const options: HTMLElement[] = Array.from(leaves())
    .filter((element) => !before.has(element))
    .filter((element) => (element.textContent ?? '').trim().length <= 20);
  const [first] = options;
  if (!first) {
    anchor.click();   // 열어 둔 목록을 닫는다
    throw new Error('목록이 열리지 않았거나 선택지가 없다');
  }

  const labelOf = (element: HTMLElement) => (element.textContent ?? '').trim();
  const left = avoid.length
    ? options.filter((element) => !avoid.includes(labelOf(element)))
    : options;
  const pool = left.length ? left : options;
  const option = pool.find((element) => preferred.includes(labelOf(element))) ?? pool[0] ?? first;
  press(option);
  await sleep(OPEN_MS);
  return labelOf(option);
}

/**
 * 라디오 칸에서 값을 고른다.
 *
 * `OptionBox` 는 선택지마다 `data-el-option` 에 **값**을 달고 있고 핸들러가
 * `onClick` 이다 — 여는 동작도, mousedown 도 필요 없다. 보이는 글자가 아니라
 * 값으로 고른다는 점이 `ComboBox` 와 다르다.
 */
export async function selectRadio(anchor: HTMLElement, value: string): Promise<void> {
  const choices = Array.from(anchor.querySelectorAll<HTMLElement>('[data-el-option]'))
    .map((element) => ({
      element,
      value: element.getAttribute('data-el-option') ?? '',
      label: (element.textContent ?? '').trim(),
    }));
  // 값으로도 보이는 글자로도 고를 수 있어야 한다. 부르는 쪽이 아는 것은 대개 글자다.
  const exact = choices.find((choice) => choice.value === value || choice.label === value);
  const near = exact
    ? []
    : choices.filter((choice) => matches(choice.value, value) || matches(choice.label, value));
  const option = exact ?? (near.length === 1 ? near[0] : undefined);
  if (!option) {
    // 내부 코드(`H`, `B`)만 돌려주면 사용자도 고를 수 없다. 보이는 글자를 준다.
    throw new OptionNotFound(value, choices.map((choice) => choice.label || choice.value));
  }
  option.element.click();
  await sleep(OPEN_MS);
}

/**
 * 체크 칸을 켜거나 끈다.
 *
 * `CheckBox` 도 입력칸이 아닌 `<div>` 이고 `ComboBox` 와 달리 목록이 열리지 않는다 —
 * 누르면 그 자리에서 뒤집힌다. 상태값은 `'1'`/`'0'` 이다. 실제 앱에서 확인했다.
 *
 * 이미 원하는 값이면 누르지 않는다. 누를 때마다 뒤집히므로 확인 없이 누르면
 * 켜려던 것이 꺼진다.
 */
export async function toggleCheck(anchor: HTMLElement, current: string, wanted: string): Promise<void> {
  if (current === wanted) return;
  press(anchor);
  await sleep(OPEN_MS);
}
